import { withTranslation } from '@application/i18n/with_translation';
import { useTitle } from '@application/use_title';
import { ActionCard, ButtonArrange, Flex, Form, InputTextArea, Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { ConditionInfoScreen } from '@component/condition_info_screen.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { ReceptionConditionInfoScreenFlow, type UnitConfigApiModel } from '@visiba/api-vcs-client-v2';
import { useResolveController, withNewContainer } from '@visiba/instantiation';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { CareNavigationRespondentGate } from './care-navigation-respondent-gate';
import { ScreenDone } from './screen_done';
import { ScreenError } from './screen_error';
import { ScreenLoading } from './screen_loading';
import { CareNavigationScreen, ViewCareNavigationController } from './view_care_navigation.controller';

const DESCRIPTION_MAX_LENGTH = 4000;

export const ViewCareNavigation = withNewContainer(function ViewCareNavigation() {
	useTitle(withTranslation('View.CreateNewMessage.Title'));

	return (
		<CareNavigationRespondentGate>
			{(onAbort, onComplete, unitConfig) => <CareNavigationFlow onAbort={onAbort} onComplete={onComplete} unitConfig={unitConfig} />}
		</CareNavigationRespondentGate>
	);
});

const CareNavigationFlow = withNewContainer(function CareNavigationFlow({
	onAbort,
	onComplete,
	unitConfig,
}: {
	onAbort: () => void;
	onComplete: () => void;
	unitConfig: UnitConfigApiModel | undefined;
}) {
	useResolveController(ViewCareNavigationController, unitConfig);

	return (
		<Flex grow='1'>
			<TopBar.Content title={withTranslation('View.CreateNewMessage.Title')} onAbort={onAbort} />

			<Routes>
				<Route index element={<ScreenLoading showSpinner={false} />} />
				<Route path={CareNavigationScreen.ReasonForContact} element={<ScreenReasonForContact />} />
				<Route path={CareNavigationScreen.DescribeMatter} element={<ScreenDescribeMatter />} />
				<Route path={CareNavigationScreen.Loading} element={<ScreenLoadingOrError />} />
				<Route path={CareNavigationScreen.Done} element={<ScreenDoneWrapper onComplete={onComplete} />} />
				<Route path='*' element={<Navigate to={CareNavigationScreen.ReasonForContact} replace />} />
			</Routes>
		</Flex>
	);
});

function ScreenLoadingOrError() {
	const controller = useResolveController(ViewCareNavigationController);
	const state = controller.flowState.value;

	if (state.state === 'error') {
		return <ScreenError title='Unable to start care navigation' />;
	}

	return <ScreenLoading />;
}

function ScreenDoneWrapper({ onComplete }: { onComplete: () => void }) {
	const controller = useResolveController(ViewCareNavigationController);

	useEffect(onComplete, [onComplete]);

	return <ScreenDone onClose={() => controller.goToReceptionHome()} />;
}

function ScreenReasonForContact() {
	const controller = useResolveController(ViewCareNavigationController);
	const state = controller.flowState.value;

	if (state.state === 'loading-initial') {
		return <ScreenLoading showSpinner={false} />;
	}

	if (state.state === 'starting-navigation') {
		return <ScreenLoading />;
	}

	if (state.state === 'done') {
		return <Navigate to={CareNavigationScreen.Done} replace />;
	}

	if (state.state === 'error') {
		return <ScreenError title='Unable to load reasons for contact' />;
	}

	if (state.state === 'describe-matter') {
		return <Navigate to={controller.getDescribeMatterRoute()} replace />;
	}

	if (state.state === 'info-screen') {
		return (
			<ConditionInfoScreen
				condition={state.reasonForContact}
				flow={ReceptionConditionInfoScreenFlow.Navigation}
				onBack={() => controller.backToReasonForContactSelection()}
				onCancel={() => controller.goToReceptionHome()}
			/>
		);
	}

	return (
		<Flex gap={tokens.spacing.x1_5}>
			<TopBar.Content goBack />

			<Title
				title={withTranslation('View.CreateNewMessage.ScreenRfv.Title')}
				titleSize='small'
				description={withTranslation('View.CreateNewMessage.ScreenRfv.Description')}
			/>

			<Spacer vertical={tokens.spacing.x0_5} />

			<Flex gap={tokens.spacing.x1}>
				{state.reasonForContacts.map((reasonForContact) => (
					<ActionCard.Root key={reasonForContact.Id} onClick={() => controller.selectReasonForContact(reasonForContact)}>
						<ActionCard.Title>{reasonForContact.Name}</ActionCard.Title>
					</ActionCard.Root>
				))}
			</Flex>
		</Flex>
	);
}

function ScreenDescribeMatter() {
	const controller = useResolveController(ViewCareNavigationController);
	const state = controller.flowState.value;
	const selectedReasonForContact =
		state.state === 'describe-matter' ? state.reasonForContact : controller.selectedReasonForContact.value;

	async function handleSubmit() {
		if (!controller.descriptionOfMatter.value.trim()) {
			return Err(new Error(withTranslation('Form.ValidationError.Required')));
		}
		return controller.startNavigation();
	}

	if (state.state === 'loading-initial') {
		return <ScreenLoading showSpinner={false} />;
	}

	if (state.state === 'starting-navigation') {
		return <ScreenLoading />;
	}

	if (state.state === 'select-reason-for-contact') {
		return <Navigate to={CareNavigationScreen.ReasonForContact} replace />;
	}

	if (state.state === 'error') {
		return <ScreenError title='Unable to start care navigation' />;
	}

	return (
		<Flex gap={tokens.spacing.x1_5} grow='1'>
			<TopBar.Content goBack />

			<Title
				title={withTranslation('View.CreateNewMessage.ScreenDescribeMatter.Title')}
				titleSize='small'
				description={withTranslation('View.CreateNewMessage.ScreenDescribeMatter.Description')}
			/>
			{selectedReasonForContact ? (
				<Text.p variant='bodyRegular'>
					{withTranslation('View.CreateNewMessage.ScreenRfv.Title')}: {selectedReasonForContact.Name}
				</Text.p>
			) : null}

			<Form.Root onSubmit={handleSubmit} options={{}}>
				<InputTextArea
					name='descriptionOfMatter'
					label={withTranslation('View.CreateNewMessage.ScreenDescribeMatter.Input.Label.DescriptionOfMatter')}
					defaultValue={controller.descriptionOfMatter.value}
					onChange={(event) => controller.setDescriptionOfMatter(event.target.value)}
					minHeight={200}
					maxHeight={400}
					autoHeight
					characterCount={DESCRIPTION_MAX_LENGTH}
					options={{
						maxLength: {
							value: DESCRIPTION_MAX_LENGTH,
							message: `Max ${DESCRIPTION_MAX_LENGTH} characters`,
						},
					}}
					required
				/>

				<Form.Error />

				<ButtonArrange>
					<Form.SubmitButton loading={controller.screenState.value === 'busy'}>
						{withTranslation('Messaging.ConversationStart.Conclusion.Submit.Button')}
					</Form.SubmitButton>
				</ButtonArrange>
			</Form.Root>
		</Flex>
	);
}
